/**
 * Проекция Web Mercator: перевод координат в пиксели тайловой карты.
 *
 * ## Зачем свои вычисления
 *
 * Карта с ДВУМЯ точками (#1330). Встраиваемый OSM (`export/embed.html`)
 * принимает bbox, но показывает не его: Leaflet внутри подбирает ближайший
 * целый зум, при котором bbox помещается, и центрирует вид. Какой именно зум
 * он выбрал, снаружи iframe не узнать — фрейм с чужого домена. Пока точка
 * одна и bbox симметричен вокруг неё, метку можно ставить в центр кадра и не
 * ошибиться. Для двух точек это уже не работает: пин, положенный «на глаз»,
 * на карте Европы промахивается на страну.
 *
 * Поэтому карту складываем из тайлов сами — тогда и зум, и начало координат
 * известны, и метки встают ровно там, где стоят салоны.
 *
 * Формулы — стандартный Web Mercator (EPSG:3857), тот же, что у OSM.
 */

/** Размер тайла OSM в пикселях. */
export const TILE_SIZE = 256;

/** Предел проекции: у Меркатора полюса уходят в бесконечность. */
const MAX_LAT = 85.0511287798;

const clampLat = (lat) => Math.min(MAX_LAT, Math.max(-MAX_LAT, lat));

/** Долгота → X в пикселях мировой карты на данном зуме. */
export function lonToX(lon, zoom) {
  return ((lon + 180) / 360) * TILE_SIZE * 2 ** zoom;
}

/** Широта → Y в пикселях мировой карты на данном зуме. */
export function latToY(lat, zoom) {
  const sin = Math.sin((clampLat(lat) * Math.PI) / 180);
  return (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * TILE_SIZE * 2 ** zoom;
}

/**
 * Наибольший целый зум, при котором все точки помещаются в кадр с полями.
 *
 * Считаем от большего зума к меньшему, а не по формуле: зумы дискретны, и
 * округление вниз по одной оси может не пройти по другой.
 *
 * @param {Array<{lat:number, lon:number}>} points
 * @param {number} width — ширина кадра в пикселях.
 * @param {number} height — высота кадра.
 * @param {object} [options]
 * @param {number} [options.padding] — поля от края кадра до крайней точки.
 * @param {number} [options.maxZoom] — не приближать ближе (одна точка иначе
 *   уводит зум в максимум, где виден один двор).
 * @returns {number} зум от 0 до `maxZoom`.
 */
export function fitZoom(points, width, height, { padding = 48, maxZoom = 12 } = {}) {
  if (!points.length || width <= 0 || height <= 0) return 0;

  const usableW = Math.max(1, width - padding * 2);
  const usableH = Math.max(1, height - padding * 2);

  for (let zoom = maxZoom; zoom > 0; zoom -= 1) {
    const xs = points.map((p) => lonToX(p.lon, zoom));
    const ys = points.map((p) => latToY(p.lat, zoom));
    const spanX = Math.max(...xs) - Math.min(...xs);
    const spanY = Math.max(...ys) - Math.min(...ys);
    if (spanX <= usableW && spanY <= usableH) return zoom;
  }
  return 0;
}

/**
 * Кадр карты: зум, смещение мировой сетки и положение каждой метки.
 *
 * @param {Array<{lat:number, lon:number}>} points
 * @param {number} width
 * @param {number} height
 * @param {object} [options] — передаётся в `fitZoom`.
 * @returns {{zoom:number, originX:number, originY:number, pins:Array<{x:number,y:number}>}}
 *   `originX/Y` — координата левого верхнего угла кадра в мировых пикселях;
 *   `pins` — координаты меток ВНУТРИ кадра.
 */
export function mapViewport(points, width, height, options) {
  const zoom = fitZoom(points, width, height, options);
  const xs = points.map((p) => lonToX(p.lon, zoom));
  const ys = points.map((p) => latToY(p.lat, zoom));

  // Центр по крайним точкам, а не среднее: среднее уводит кадр к сгущению,
  // и одиночная дальняя точка оказывается у самого края.
  const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
  const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;

  const originX = centerX - width / 2;
  const originY = centerY - height / 2;

  return {
    zoom,
    originX,
    originY,
    pins: points.map((p, i) => ({ x: xs[i] - originX, y: ys[i] - originY })),
  };
}
